import React from 'react';
import { Alert, Button, Stack, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { LegalItem, LegalPage, LegalSection, LegalText } from '@src/components/LegalPage';

const PRIVACY_EMAIL = 'supago@kabandu.com';

/**
 * Solicitud de eliminación de datos (derechos ARCO).
 *
 * OJO — antes esta pantalla tenía un FORMULARIO SIMULADO: esperaba 1.5 s con un
 * `setTimeout` y respondía «hemos recibido tu solicitud, te contactamos en 5 días
 * hábiles» sin enviar nada a ningún lado. Nadie recibía esas solicitudes.
 *
 * Se reemplazó por el camino que sí llega a una persona: un correo al buzón de privacidad,
 * con asunto y cuerpo prellenados para que la clienta no tenga que adivinar qué datos
 * incluir. Si más adelante se quiere un formulario de verdad, hace falta un endpoint
 * público en el server que envíe ese correo (con su protección anti-bots).
 */
const DataDeletion: React.FC = () => {
    const subject = encodeURIComponent('Solicitud de eliminación de datos personales');
    const body = encodeURIComponent(
        [
            'Hola, solicito la eliminación de mis datos personales de los sistemas de Kabandú.',
            '',
            'Nombre completo:',
            'Teléfono registrado:',
            'Correo registrado:',
            '',
            'Gracias.'
        ].join('\n')
    );

    return (
        <LegalPage
            title="Eliminar mis datos"
            intro="Puedes pedirnos que eliminemos tus datos personales cuando quieras. Aquí te decimos cómo y qué pasa después."
        >
            <LegalSection title="Tu derecho">
                <LegalText>
                    De acuerdo con la Ley Federal de Protección de Datos Personales en Posesión de los Particulares,
                    puedes solicitar el Acceso, Rectificación, Cancelación u Oposición (derechos ARCO) al uso de tus
                    datos personales.
                </LegalText>
            </LegalSection>

            <LegalSection title="Cómo solicitarlo">
                <LegalText>
                    Escríbenos a <strong>{PRIVACY_EMAIL}</strong> desde el correo que tienes registrado, o desde
                    cualquiera indicando tus datos. El botón deja el mensaje listo, solo completas tu información:
                </LegalText>
                <Stack spacing={1.5} sx={{ pt: 0.5 }}>
                    <LegalItem title="1. Nos escribes">
                        Incluye tu nombre completo y el teléfono o correo con el que estás registrada, para poder
                        identificar tu cuenta.
                    </LegalItem>
                    <LegalItem title="2. Verificamos tu identidad">
                        Te contactamos para confirmar que la solicitud es tuya. Nunca eliminamos datos sin esa
                        confirmación.
                    </LegalItem>
                    <LegalItem title="3. Eliminamos tus datos">
                        En un plazo máximo de 5 días hábiles después de la confirmación, y te avisamos cuando quede
                        hecho.
                    </LegalItem>
                </Stack>

                <Button
                    component="a"
                    href={`mailto:${PRIVACY_EMAIL}?subject=${subject}&body=${body}`}
                    variant="contained"
                    size="large"
                    startIcon={<MailOutlineIcon />}
                    sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' }, mt: 1 }}
                >
                    Escribir a privacidad
                </Button>
            </LegalSection>

            <LegalSection title="Qué se conserva y por qué">
                <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
                    Por obligaciones fiscales, los comprobantes de tus compras y servicios deben conservarse 5 años.
                    Esos registros se anonimizan —dejan de estar asociados a ti— y no se usan para ningún otro
                    propósito.
                </Alert>
            </LegalSection>

            <LegalSection title="Dudas">
                <LegalText>
                    Nuestro Oficial de Privacidad responde en <strong>{PRIVACY_EMAIL}</strong>. También puedes revisar
                    el{' '}
                    <Typography component={Link} to="/privacy" color="text.primary" sx={{ fontWeight: 600 }}>
                        Aviso de Privacidad
                    </Typography>{' '}
                    completo.
                </LegalText>
            </LegalSection>
        </LegalPage>
    );
};

export default DataDeletion;
